import React, {
  CSSProperties,
  FC,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Combobox,
  makeStyles,
  Option,
  useId,
  Spinner,
} from "@fluentui/react-components";
import type {
  ComboboxProps,
  OptionOnSelectData,
  ComboboxOpenChangeData,
} from "@fluentui/react-components";

const useStyles = makeStyles({
  root: {
    // Stack the label above the field with a gap
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    gap: "2px",
    maxWidth: "max-content",
    minWidth: "min-content",
  },
  combobox: {
    minWidth: "200px",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "40px", // Height similar to the dropdown
  },
});
interface OptionType {
  label: string;
  value: string;
}
interface CustomDropDownProps extends Partial<ComboboxProps> {
  label?: string;
  optionsFetcher?: () => Promise<OptionType[]>;
  values?: OptionType[];
  onSelectionChange?: (selectedValue: string) => void;
  className?: string; // Optional custom class
  style?: CSSProperties; // Optional inline styles
  multiSelect?: boolean;
}
const ASCDropDown: FC<CustomDropDownProps> = ({
  label,
  optionsFetcher,
  values,
  onSelectionChange,
  className,
  style,
  multiSelect = false,
  ...rest
}) => {
  const comboId = useId("combo-default");

  const styles = useStyles();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const handleOptionSelect = (
    event: SyntheticEvent<HTMLElement>,
    data: OptionOnSelectData
  ) => {
    const clickedOption = options.find(
      (option) => option.label === data.optionValue
    );

    if (!clickedOption) return;

    setSelectedValues((prev) => {
      let updated: any;
      if (prev.includes(clickedOption.value)) {
        updated = prev.filter((v) => v !== clickedOption.value); // remove
      } else {
        updated = [...prev, clickedOption.value]; // add
      }

      onSelectionChange?.(updated); // send array to parent
      return updated;
    });
  };

  const handleOpenChange = async (
    event: SyntheticEvent<HTMLElement>,
    data: ComboboxOpenChangeData
  ) => {
    if (data.open && options.length === 0) {
      setLoading(true);
      try {
        if (optionsFetcher) {
          const fetchedOptions = await optionsFetcher();
          setOptions(fetchedOptions);
        } else if (values) {
          setOptions(values); // Use provided values if no fetcher
        }
      } catch (error) {
        console.error("Failed to fetch options:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <div className={`${styles.root} ${className || ""}`} style={style}>
      {label && <label id={comboId}>{label}</label>}

      <Combobox
        aria-labelledby={comboId}
        onOptionSelect={handleOptionSelect}
        onOpenChange={handleOpenChange}
        className={styles.combobox}
        multiselect={multiSelect}
        selectedOptions={options
          .filter((o) => selectedValues.includes(o.value))
          .map((o) => o.label)}
        {...rest}
      >
        {loading ? (
          <div className={styles.loaderContainer}>
            <Spinner size="medium" />
          </div>
        ) : (
          options.map((option) => (
            <Option key={option.value}>{option.label}</Option>
          ))
        )}
      </Combobox>
    </div>
  );
};
export default ASCDropDown;
